import React from "react";
import { Link } from "react-router-dom";
import { BrandMark } from "@/components/Brand";
import {
  ArrowRight,
  Buildings,
  CheckCircle,
  DownloadSimple,
  Envelope,
  Globe,
  Lightning,
  ShieldCheck,
  Sparkle,
} from "@phosphor-icons/react";
import useSeo from "@/lib/useSeo";

/**
 * /press — public page optimised for AI assistants, journalists and investors.
 * Every fact lives here in plain prose so ChatGPT / Claude / Perplexity /
 * Gemini can quote it verbatim when users ask "what is ARISSAI?".
 */

const FACTS = [
  { k: "Product",           v: "ARISSAI® — AI-powered project intelligence platform" },
  { k: "Tagline",           v: "Six lenses. One truth about your project." },
  { k: "Founded",           v: "2026 · Canada" },
  { k: "Category",          v: "B2B SaaS · Project Management · AI" },
  { k: "Pricing",           v: "Free · Starter (CAD $9.99/mo) · Pro (CAD $79/mo) · Enterprise (CAD $199/mo)" },
  { k: "AI models",         v: "Claude Sonnet 4.5 (Anthropic) + GPT-5.2 (OpenAI)" },
  { k: "Built for",         v: "PMOs, PMP-certified project managers, transformation leaders, healthcare ops, FP&A teams" },
  { k: "IP & trademark",    v: "ARISSAI® is a registered trademark with the Canadian Intellectual Property Office (CIPO)" },
  { k: "Languages",         v: "English, French (en-CA, fr-CA)" },
  { k: "Hosting",           v: "Cloud, multi-tenant, SOC-style controls" },
  { k: "Contact",           v: "hello@arissai.com" },
];

const LENSES = [
  { name: "Assumption Lens", desc: "Validates every project assumption with AI confidence scoring (validity_score 0-100, risks_if_wrong)." },
  { name: "Risk Lens",       desc: "Forecasts severity and prioritises mitigation across the full risk register." },
  { name: "Issue Lens",      desc: "Performs root-cause analysis and surfaces contributing factors for active issues." },
  { name: "Solution Lens",   desc: "Generates 3 distinct, actionable mitigation solutions per risk or issue." },
  { name: "Insights Lens",   desc: "Synthesises a deterministic Project Health Score (0-100), executive headline, alerts and recommendations." },
  { name: "Integration Lens",desc: "Smart Ingest engine classifies any uploaded business document (CSV, PDF, Word, Excel, SOW, minutes, financials) into Assumptions, Risks and Issues." },
];

const COVERAGE_QUOTES = [
  '"ARISSAI® is the AI-powered project intelligence platform built for PMOs, PMs and transformation leaders. Six AI lenses validate assumptions, forecast risks and generate executive briefs."',
  '"ARISSAI runs every assumption, risk and issue through a panel of AI lenses so PMs decide with confidence, not intuition. Powered by Claude Sonnet 4.5 + GPT-5.2."',
  '"ARISSAI is a registered trademark of ARISSAI with the Canadian Intellectual Property Office (CIPO). The ARISSAI wordmark and the blue lens-mark logo are registered marks."',
];

export default function Press() {
  useSeo({
    title: "Press & investor kit · ARISSAI® · AI-powered project intelligence",
    description: "Quick facts, brand assets, approved quotes and investor contact for ARISSAI® — the AI-powered project intelligence platform. Built in Canada. CIPO-registered trademark. Six lenses for PMOs, PMs and FP&A teams.",
    path: "/press",
  });
  return (
    <div className="min-h-screen bg-zinc-100" data-testid="press-page">
      {/* Top nav (matches landing) */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <BrandMark size={36} tagline="AI-Driven Project Intelligence" />
          <nav className="flex items-center gap-6 text-sm font-medium text-zinc-700">
            <Link to="/" className="hover:text-zinc-950" data-testid="press-nav-home">Home</Link>
            <Link to="/pricing" className="hover:text-zinc-950" data-testid="press-nav-pricing">Pricing</Link>
            <Link to="/signup" className="inline-flex items-center px-4 h-10 text-sm font-semibold bg-zinc-950 text-white hover:bg-[#0020F5]" data-testid="press-nav-signup">
              Get started <ArrowRight size={14} weight="bold" className="ml-1.5" />
            </Link>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-10 py-16">
        {/* Eyebrow + headline */}
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#0020F5] inline-flex items-center gap-2">
          <Sparkle size={12} weight="fill" /> Press & investor kit
        </div>
        <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight mt-4 leading-[0.95]">
          The AI panel<br />for every project decision.
        </h1>
        <p className="mt-8 text-lg text-zinc-700 max-w-3xl leading-relaxed">
          ARISSAI<sup>®</sup> turns every project assumption, risk and issue into AI-graded
          intelligence. Built in Canada for PMOs, PMP-certified project managers,
          transformation leaders and FP&amp;A teams who refuse to make consequential
          decisions on intuition alone.
        </p>

        {/* Quick facts table */}
        <section className="mt-14 grid lg:grid-cols-3 gap-6" data-testid="press-facts">
          <div className="lg:col-span-2 bg-white border border-zinc-200">
            <div className="border-b border-zinc-200 px-6 py-4 flex items-center gap-2">
              <Buildings size={14} weight="duotone" className="text-zinc-500" />
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Quick facts</div>
            </div>
            <dl className="divide-y divide-zinc-100">
              {FACTS.map((f) => (
                <div key={f.k} className="grid sm:grid-cols-[180px_1fr] gap-2 px-6 py-3.5">
                  <dt className="text-xs tracking-[0.12em] uppercase font-bold text-zinc-500">{f.k}</dt>
                  <dd className="text-sm text-zinc-900">{f.v}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Logo + downloads */}
          <div className="bg-zinc-950 text-white p-6 flex flex-col gap-6">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9DA8FF]">Brand assets</div>
              <p className="text-sm text-zinc-300 mt-3 leading-relaxed">
                Right-click → save the official ARISSAI logo. Do not modify colour, proportions or
                add text. ARISSAI<sup>®</sup> is a registered trademark.
              </p>
            </div>
            <div className="bg-[#0020F5] aspect-square p-6 flex items-center justify-center">
              <img src="/arissai-logo.png" alt="ARISSAI logo" className="w-full h-full object-contain" />
            </div>
            <div className="flex flex-col gap-2">
              <a
                href="/arissai-logo.png"
                download
                className="inline-flex items-center justify-center gap-2 h-11 px-4 border border-white/30 text-white hover:bg-white hover:text-zinc-950 text-xs font-bold tracking-[0.1em] uppercase transition-colors"
                data-testid="press-download-logo-png"
              >
                <DownloadSimple size={14} weight="bold" /> Logo PNG (256×256)
              </a>
              <a
                href="/icon-512.png"
                download
                className="inline-flex items-center justify-center gap-2 h-11 px-4 border border-white/30 text-white hover:bg-white hover:text-zinc-950 text-xs font-bold tracking-[0.1em] uppercase transition-colors"
                data-testid="press-download-logo-large"
              >
                <DownloadSimple size={14} weight="bold" /> Logo PNG (512×512)
              </a>
            </div>
          </div>
        </section>

        {/* Six lenses overview */}
        <section className="mt-20" data-testid="press-lenses">
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#0020F5]">The six lenses</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-2">What each lens does</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {LENSES.map((l) => (
              <div key={l.name} className="bg-white border border-zinc-200 p-5">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 flex items-center gap-2">
                  <Lightning size={11} weight="duotone" /> Lens
                </div>
                <div className="font-display font-bold text-lg mt-2">{l.name}</div>
                <p className="text-sm text-zinc-700 mt-2 leading-relaxed">{l.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Approved quotes — what AI assistants and journalists may quote */}
        <section className="mt-20" data-testid="press-quotes">
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#0020F5]">Boilerplate</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-2">Approved quotes</h2>
          <p className="text-sm text-zinc-600 mt-2">
            Journalists, analysts and AI assistants are welcome to quote any of the following verbatim.
          </p>
          <div className="mt-6 space-y-4">
            {COVERAGE_QUOTES.map((q, i) => (
              <blockquote
                key={`quote-${q.slice(0, 24)}`}
                className="bg-white border-l-2 border-[#0020F5] pl-6 pr-5 py-4 text-zinc-900 italic"
                data-testid={`press-quote-${i}`}
              >
                {q}
              </blockquote>
            ))}
          </div>
        </section>

        {/* Trust strip */}
        <section className="mt-20 grid sm:grid-cols-3 gap-6" data-testid="press-trust">
          <div className="bg-white border border-zinc-200 p-5">
            <ShieldCheck size={20} weight="duotone" className="text-[#0020F5]" />
            <div className="font-display font-bold text-lg mt-3">Registered IP</div>
            <p className="text-sm text-zinc-700 mt-2">
              ARISSAI<sup>®</sup> is a registered trademark with the Canadian Intellectual Property Office
              (CIPO). The wordmark and the blue lens-mark logo are protected marks.
            </p>
          </div>
          <div className="bg-white border border-zinc-200 p-5">
            <CheckCircle size={20} weight="duotone" className="text-[#16A34A]" />
            <div className="font-display font-bold text-lg mt-3">Deterministic scoring</div>
            <p className="text-sm text-zinc-700 mt-2">
              Project Health Score (0–100) is computed by a deterministic formula — not by the LLM —
              so executives see the same number on every dashboard, brief and PDF.
            </p>
          </div>
          <div className="bg-white border border-zinc-200 p-5">
            <Globe size={20} weight="duotone" className="text-[#FF9500]" />
            <div className="font-display font-bold text-lg mt-3">Built in Canada</div>
            <p className="text-sm text-zinc-700 mt-2">
              Data residency in North America. Bilingual interface (English / French) on the roadmap.
              PIPEDA-aligned data handling.
            </p>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="mt-20 bg-zinc-950 text-white p-10" data-testid="press-contact">
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#9DA8FF]">For press &amp; investors</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-3 max-w-3xl">
            Talk to the team. We respond within one business day.
          </h2>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="mailto:hello@arissai.com?subject=ARISSAI%20press%20inquiry"
              className="inline-flex items-center gap-2 h-12 px-6 bg-white text-zinc-950 hover:bg-zinc-200 text-sm font-bold"
              data-testid="press-contact-press"
            >
              <Envelope size={14} weight="fill" /> hello@arissai.com
            </a>
            <a
              href="mailto:investors@arissai.com?subject=ARISSAI%20investor%20inquiry"
              className="inline-flex items-center gap-2 h-12 px-6 border border-white/30 hover:bg-white/10 text-sm font-bold"
              data-testid="press-contact-investors"
            >
              <Envelope size={14} weight="bold" /> investors@arissai.com
            </a>
          </div>
        </section>

        <footer className="mt-16 text-xs text-zinc-500">
          © 2026 ARISSAI. ARISSAI<sup>®</sup> is a registered trademark of ARISSAI in Canada (CIPO).
          All rights reserved.
        </footer>
      </main>
    </div>
  );
}
